/* eslint-disable @typescript-eslint/no-explicit-any */
import { HeadersFunction, isRouteErrorResponse, MetaFunction, useLoaderData, useRouteError } from "react-router";
import { ArticleSchema } from "~/components/ArticleSchema";
import ArticleView from "~/components/blog/ArticalView";
import NodataFound from "~/components/shared-components/NoDataFound";
import { ArticleType, RelatedArticleType } from "~/interface/article/article";
import { MstPersonType } from "~/type";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { computeEtagFromData } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { orgSchemaProps } from "~/utils/organizationSchema";
import { ApiService } from "~/utils/service";

export interface LoaderData {
  articleData: ArticleType | null;
  title: string;
  authorName: string | null;
  relatedArticle: RelatedArticleType[];
  blogAuthor: MstPersonType[];
  tagsData: MstPersonType[];
  placeName: string | null;
}

export const meta: MetaFunction = ({ location, data }) => {
  const pathname = location?.pathname || "/";

  const { articleData } = data as LoaderData;

  return metaService({
    title: articleData?.meta_title || articleData?.title || "",
    description: articleData?.meta_desc || "",
    ogTitle: articleData?.meta_title || articleData?.title || "",
    ogDescription: articleData?.meta_desc || "",
    ogUrl: pathname,
    ogImg: articleData?.thumb_img || FALLBACK_IMAGE_URL,
    canonicalLink: pathname,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const loader = async ({ params, request }: { params: { lang: string; slug: string }; request: Request }): Promise<any> => {
  const { lang, slug } = params;

  const apiService = new ApiService(lang || LANG.english);
  const response: ArticleType = await apiService.getArticleDataBaseOnId("news", slug);

  const mstResponse = await apiService.getMasterData();
  const relatedArticle = await apiService.getRelatedArticle(slug);

  if (!response) {
    throw new Response(`news detail not found`, {
      status: 404,
      statusText: `news detail not found`,
    });
  }

  const blogAuthor: MstPersonType[] = mstResponse?.blog_author;
  const newsCategory: MstPersonType[] = mstResponse?.news_category;
  const placeData: MstPersonType[] = mstResponse?.place;
  const authorName = blogAuthor?.find((item) => item.id === response?.author)?.text || null;
  const placeName = placeData?.find((item) => item.id === response?.place_id)?.text || null;
  const tags = newsCategory || [];

  const DATA = {
    articleData: response || null,
    title: response.title || slug,
    authorName: authorName,
    relatedArticle: relatedArticle,
    blogAuthor: blogAuthor,
    tagsData: tags || [],
    placeName: placeName,
  };

  const etag = computeEtagFromData(DATA);
  const ifNoneMatch = request.headers.get("if-none-match");
  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: { ...CACHED_PAGE.NEWS.HEADERS, ETag: etag },
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

// export function headers({ loaderHeaders }: { loaderHeaders: Headers }) {
//   return {
//     "Cache-Control": loaderHeaders.get("Cache-Control") ?? cacheHeaders.none,
//     ETag: cacheHeaders.version,
//   };
// }

const BlogDetailPage = () => {
  const { articleData, title, authorName, relatedArticle, blogAuthor, tagsData, placeName } = useLoaderData<LoaderData>();

  return (
    <>
      <ArticleSchema
        type="NewsArticle"
        headline={title || articleData?.meta_title || "News Article"}
        description={articleData?.meta_desc}
        image={
          [articleData?.img?.s?.url, articleData?.img?.m?.url, articleData?.img?.l?.url, articleData?.img?.hd?.url, articleData?.img?.og?.url].filter(
            Boolean,
          ) as string[]
        }
        datePublished={articleData?.write_date}
        author={authorName ? { name: authorName } : undefined}
        publisher={{
          name: orgSchemaProps.name,
          logo: orgSchemaProps.logo,
          url: orgSchemaProps.url,
        }}
        keywords={articleData?.tag}
        inLanguage={articleData?.lang}
        wordCount={articleData?.read_min ? articleData.read_min * 200 : undefined}
      />
      <ArticleView
        articleData={articleData}
        title={title}
        authorName={authorName}
        relatedArticle={relatedArticle}
        blogAuthor={blogAuthor}
        tagsData={tagsData}
        placeName={placeName}
      />
    </>
  );
};

export function ErrorBoundary() {
  const error = useRouteError();
  let errorMessage = "An unexpected error occurred";

  if (isRouteErrorResponse(error)) {
    errorMessage = error.data;
  } else if (error instanceof Error) {
    errorMessage = String(error.message);
  }
  return <NodataFound text={errorMessage || "No Data Found"} />;
}

export default BlogDetailPage;
